// Port 2 (Partikel) — Jans prozedurale Effekte, aus seinem Renderer uebernommen.
//
// HERKUNFT
//
// pro-editor/src/storyforger/Renderer/RendererWebGL.ts — sechs Methoden
// (updateEffect, syncEffect, syncInstanced, syncSmoke, makeParticleTexture,
// makeFlakeTexture), zwei Modul-Helfer und ein Typ. Zusammen 890 Zeilen.
//
// WARUM ALS EIGENES MODUL
//
// Gemessen vor dem Port: Der Code greift in 826 Zeilen genau ZEHNMAL nach
// aussen — dreimal `layerContentCache`, viermal `layerMeshes`, dreimal
// `props`, dazu fuenf Aufrufe untereinander. Sonst nichts. Er ist damit
// praktisch eigenstaendig und gehoert nicht in die ohnehin 3500 Zeilen lange
// Renderer-Klasse.
//
// Fuenf der sechs sind REINE Funktionen — sie fassen den Renderer gar nicht an.
// Nur `updateEffect` braucht Zustand (zehn Zugriffe) und bekommt ihn als
// `host`-Parameter. Das ist
// die EINZIGE Umformung gegenueber Jans Fassung (`this.` → `host.`) — genau
// deshalb laesst sie sich maschinell gegenpruefen, wie bei der
// Infografik-Engine (siehe src/pro-editor-compat/*-sync.spec.ts im
// Plattform-Repo).
//
// WAS DER PORT NICHT LEISTET
//
// Partikel sind WebGL. Ob Konfetti richtig faellt, kann kein Test sagen —
// das Ergebnis existiert nur auf einer Grafikkarte. Die Pruefung ist
// visuell, in mehreren Browsern.
//
// GATE (gemessen 2026-07-21 gegen die dev-Datenbank): Von 214 bestehenden
// Scrollys nutzt KEINER effect-Layer. Der `case` in `renderLayer` kann den
// Bestand also nicht veraendern.
import THREE from "../utils/THREE";
import type { EffectContent } from "@scrolly/post-types";
import { WebGLAssets } from "../Player/webglAssets";
import RendererStore from "../Player/RendererStore";
import { istMobilesGeraet } from "../utils/geraet";
import { edgeAlphaFor, type SpawnShape } from "./spawnShape";

/**
 * Der Teil des Renderers, den die Effekte anfassen — mehr brauchen sie nicht.
 * Nur `updateEffect` benutzt ihn; die uebrigen fuenf Funktionen sind rein.
 * Die Renderer-Klasse erfuellt diese Form von sich aus.
 */
export interface EffectHost {
    // Bewusst Object3D, nicht Mesh: unser Renderer legt in `layerMeshes` auch
    // Lichter ab, und die Partikelsysteme selbst sind Points bzw. InstancedMesh
    // — beides keine Meshes. Object3D ist der gemeinsame Nenner, den beide
    // Seiten tatsaechlich erfuellen.
    layerMeshes: { [id: string]: THREE.Object3D };
    layerContentCache: { [id: string]: unknown };
    props: { renderer: RendererStore };
}

/**
 * Obergrenze fuer die Partikelzahl. Wird der Count-Kanal animiert, legt
 * `updateEffect` gleich so viele an — gezeichnet wird dann eine Teilmenge.
 * Aus Jans Renderer uebernommen (dort Zeile 243).
 */
export const EFFECT_COUNT_MAX = 600

interface EffectOverrides {
  size?: number
  speed?: number
  wind?: number
  depth?: number
  count?: number
  edgeFalloff?: number
  /**
   * Halbe Breite/Hoehe des SICHTBAREN Ausschnitts in Buehnen-Einheiten
   * (Jans Fund 09.09.2026: "alle 4 nur Square"). Die Streuflaeche bezog sich
   * auf die quadratische 4096er-Buehne; im Querformat sieht man davon nur
   * ein Band in der Mitte, das Kreis und Herz fast ganz fuellten — die Form
   * war nie zu sehen. Mit den sichtbaren Halbmassen passt die Form in den
   * Ausschnitt (kleinere Kante = Formgroesse), im Editor wie im Leser je
   * Geraete-Format. Fehlen sie, gilt die ganze Buehne wie bisher.
   */
  visibleHalfW?: number
  visibleHalfH?: number
}

/** Halbmasse der Streuflaeche: sichtbarer Ausschnitt, sonst die ganze Buehne. */
const streuHalbmasse = (
  overrides: EffectOverrides | undefined,
  slideHalfW: number,
  slideHalfH: number
): [number, number] => [
  overrides?.visibleHalfW && overrides.visibleHalfW > 0 ? overrides.visibleHalfW : slideHalfW,
  overrides?.visibleHalfH && overrides.visibleHalfH > 0 ? overrides.visibleHalfH : slideHalfH,
]


// Inject a per-particle `aEdge` (0..1) alpha multiply into a particle material's
// shader (works for PointsMaterial and the instanced quad MeshBasicMaterial).

const injectEdgeAlpha = (material: TODOAny) => {
  material.onBeforeCompile = (shader: TODOAny) => {
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        '#include <common>\nattribute float aEdge;\nvarying float vEdge;'
      )
      .replace(
        '#include <begin_vertex>',
        '#include <begin_vertex>\nvEdge = aEdge;'
      )
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying float vEdge;')
      // premultiplied_alpha_fragment exists in BOTH the points and the mesh
      // fragment shaders (dithering_fragment is mesh-only), so this multiply
      // reaches rain/sparks/fire/clouds too
      .replace(
        '#include <premultiplied_alpha_fragment>',
        'gl_FragColor.a *= vEdge;\n#include <premultiplied_alpha_fragment>'
      )
  }
}

// When the Count channel is animated we allocate the system at this maximum and
// only DRAW a subset per frame (no rebuild → no flicker). Matches the Count
// slider's max in the menu.


/**
 * Canvas-Inhalt als DataTexture uploaden statt als CanvasTexture: getImageData
 * liefert per Spec UNmultiplizierte RGBA-Bytes, waehrend der direkte
 * Canvas-Upload ueber den premultiplied Backing-Store des Browsers laeuft.
 * Auf iOS-WebKit zeigte genau dieser Weg Farb-Konfetti in den weichen
 * Alpha-Raendern der Rauch-Puffs (Jans Krisseln-Fund 26.08.2026, per
 * Ebene-Ausblenden auf den Rauch eingegrenzt) — der DataTexture-Upload ist
 * deterministisch auf allen Plattformen. Filter-Setup spiegelt das bisherige
 * CanvasTexture-Verhalten (flipY, lineare Filter).
 */
function kanvasZuTextur(canvas: HTMLCanvasElement): THREE.Texture {
  const ctx = canvas.getContext('2d')!
  const bild = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const texture = new THREE.DataTexture(
    new Uint8Array(bild.data.buffer),
    canvas.width,
    canvas.height,
    THREE.RGBAFormat,
    THREE.UnsignedByteType
  )
  texture.flipY = true
  texture.minFilter = THREE.LinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.generateMipmaps = false
  texture.needsUpdate = true
  return texture
}

export function makeParticleTexture(effect: EffectContent['effect']): THREE.Texture {
  const s = 64
  const canvas = document.createElement('canvas')
  canvas.width = s
  canvas.height = s
  const ctx = canvas.getContext('2d')!
  const c = s / 2
  if (effect === 'rain') {
    // a thin, soft vertical streak
    const grad = ctx.createLinearGradient(0, 0, 0, s)
    grad.addColorStop(0, 'rgba(255,255,255,0)')
    grad.addColorStop(0.5, 'rgba(255,255,255,1)')
    grad.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = grad
    ctx.fillRect(c - 3, 2, 6, s - 4)
  } else if (effect === 'confetti') {
    // a crisp filled disc
    ctx.fillStyle = '#fff'
    ctx.beginPath()
    ctx.arc(c, c, c - 6, 0, Math.PI * 2)
    ctx.fill()
  } else if (effect === 'smoke' || effect === 'clouds') {
    // a very soft, broad puff with no hard core (volumetric look)
    const grad = ctx.createRadialGradient(c, c, 0, c, c, c)
    grad.addColorStop(0, 'rgba(255,255,255,0.7)')
    grad.addColorStop(0.5, 'rgba(255,255,255,0.35)')
    grad.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = grad
    ctx.beginPath()
    ctx.arc(c, c, c, 0, Math.PI * 2)
    ctx.fill()
  } else {
    // snow, sparks & fire: soft radial glow (sparks/fire read as glowing
    // under additive blending)
    const grad = ctx.createRadialGradient(c, c, 0, c, c, c)
    const core = effect === 'sparks' || effect === 'fire' ? 0.32 : 0.5
    grad.addColorStop(0, 'rgba(255,255,255,1)')
    grad.addColorStop(core, 'rgba(255,255,255,0.85)')
    grad.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = grad
    ctx.beginPath()
    ctx.arc(c, c, c, 0, Math.PI * 2)
    ctx.fill()
  }
  return kanvasZuTextur(canvas)
  }

/**
 * Teilchen-Form Herz oder Stern (Jans Wunsch 09.09.2026, "Piece shape").
 * Weiss auf transparent, je Instanz eingefaerbt — wie die runde Scheibe,
 * nur mit Kontur: Herz als Parameterkurve, Stern als fuenfzackiger Umriss
 * mit einer Zacke nach oben. 128 px, damit die Kante bei grossen Teilchen
 * sauber bleibt (alphaTest schneidet sie hart).
 */
export function makePieceTexture(shape: 'heart' | 'star' | 'leaf'): THREE.Texture {
  const s = 128
  const canvas = document.createElement('canvas')
  canvas.width = s
  canvas.height = s
  const ctx = canvas.getContext('2d')!
  const c = s / 2
  ctx.fillStyle = '#fff'
  ctx.beginPath()
  if (shape === 'heart') {
    // Kurve in Einheiten -16..16 / -17..13, auf die Flaeche skaliert;
    // Canvas-Y zeigt nach unten, also Y spiegeln (Boegen oben).
    const k = (s - 8) / 34
    for (let i = 0; i <= 200; i++) {
      const t = (i / 200) * Math.PI * 2
      const x = 16 * Math.sin(t) ** 3
      const y =
        13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t)
      const px = c + x * k
      const py = c - (y + 2) * k
      if (i === 0) ctx.moveTo(px, py)
      else ctx.lineTo(px, py)
    }
  } else if (shape === 'leaf') {
    // BLATT (Jans Wunsch 17.10.2026): spitzes Blatt, leicht schraeg, mit
    // kurzem Stiel; die Mittelrippe wird ausgestanzt, damit es auch klein
    // als Blatt lesbar bleibt.
    ctx.save()
    ctx.translate(c, c)
    ctx.rotate(-Math.PI / 6)
    const L = s * 0.4 // halbe Laenge
    const B = s * 0.22 // halbe Breite
    ctx.moveTo(0, -L)
    ctx.bezierCurveTo(B * 0.8, -L * 0.3, B * 1.6, L * 0.4, 0, L * 0.8)
    ctx.bezierCurveTo(-B * 1.6, L * 0.4, -B * 0.8, -L * 0.3, 0, -L)
    ctx.closePath()
    ctx.fill()
    // Stiel
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#fff'
    ctx.lineWidth = s * 0.035
    ctx.beginPath()
    ctx.moveTo(0, L * 0.7)
    ctx.lineTo(0, L * 1.1)
    ctx.stroke()
    // Mittelrippe ausstanzen
    ctx.globalCompositeOperation = 'destination-out'
    ctx.lineWidth = s * 0.025
    ctx.beginPath()
    ctx.moveTo(0, -L * 0.72)
    ctx.quadraticCurveTo(B * 0.12, 0, 0, L * 0.62)
    ctx.stroke()
    ctx.restore()
    return kanvasZuTextur(canvas)
  } else {
    const innen = Math.cos((72 * Math.PI) / 180) / Math.cos((36 * Math.PI) / 180)
    const R = c - 4
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? R : R * innen
      const w = -Math.PI / 2 + (i * Math.PI) / 5
      const px = c + r * Math.cos(w)
      const py = c + r * Math.sin(w)
      if (i === 0) ctx.moveTo(px, py)
      else ctx.lineTo(px, py)
    }
  }
  ctx.closePath()
  ctx.fill()
  return kanvasZuTextur(canvas)
}

  // A six-armed snowflake on a transparent background (white, tinted later by
  // the per-instance colour). Used as the texture for the instanced snow quads.

export function makeFlakeTexture(): THREE.Texture {
  const s = 64
  const canvas = document.createElement('canvas')
  canvas.width = s
  canvas.height = s
  const ctx = canvas.getContext('2d')!
  ctx.translate(s / 2, s / 2)
  ctx.strokeStyle = '#fff'
  ctx.lineCap = 'round'
  ctx.lineWidth = 2.4
  const arm = s * 0.42
  for (let k = 0; k < 6; k += 1) {
    ctx.save()
    ctx.rotate((k * Math.PI) / 3)
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.lineTo(0, -arm)
    // two pairs of side branches
    ctx.moveTo(0, -arm * 0.6)
    ctx.lineTo(arm * 0.24, -arm * 0.84)
    ctx.moveTo(0, -arm * 0.6)
    ctx.lineTo(-arm * 0.24, -arm * 0.84)
    ctx.moveTo(0, -arm * 0.34)
    ctx.lineTo(arm * 0.18, -arm * 0.54)
    ctx.moveTo(0, -arm * 0.34)
    ctx.lineTo(-arm * 0.18, -arm * 0.54)
    ctx.stroke()
    ctx.restore()
  }
  // soft centre
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, arm * 0.3)
  g.addColorStop(0, 'rgba(255,255,255,0.95)')
  g.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(0, 0, arm * 0.3, 0, Math.PI * 2)
  ctx.fill()
  return kanvasZuTextur(canvas)
  }

  // Builds (and rebuilds when type/params change) a procedural particle effect
  // as a THREE.Points cloud. The geometry only holds per-particle seeds; actual
  // positions are computed every frame from the timeline timer in syncEffect(),
  // so every effect is fully deterministic and scrubbable.

export function syncInstanced(
  mesh: THREE.InstancedMesh,
  content: EffectContent,
  timer: number,
  overrides?: EffectOverrides
  ) {
  const seeds = mesh.userData.seeds as Float32Array | undefined
  if (!seeds) return
  const isSnow = (content.effect ?? 'confetti') === 'snow'
  const { SLIDE_WIDTH, SLIDE_HEIGHT } = RendererStore
  const margin = SLIDE_HEIGHT * 0.15
  const travel = SLIDE_HEIGHT + margin * 2
  const wind = overrides?.wind ?? content.wind ?? 0
  const size = overrides?.size ?? content.size ?? 26
  const vMul = isSnow ? 0.45 : 1
  const speed =
    (overrides?.speed ?? content.speed ?? 1) * 0.00018 * SLIDE_HEIGHT * vMul
  const swayAmp = SLIDE_WIDTH * (isSnow ? 0.06 : 0.04)
  const swayFreq = isSnow ? 0.0009 : 0.0016
  const windFac = isSnow ? 0.45 : 0.4
  // Herz/Stern: unverzerrt (die Kontur wuerde sonst gequetscht), Rechteck und
  // Scheibe wie bisher leicht flach.
  const formPiece =
    content.pieceShape === 'heart' || content.pieceShape === 'star' || content.pieceShape === 'leaf'
  const aspectY = isSnow || formPiece ? 1 : 0.7
  const depth = overrides?.depth ?? content.depth ?? (isSnow ? 0.3 : 0)
  const depthRange = depth * Math.min(SLIDE_WIDTH, SLIDE_HEIGHT)
  const eShape = (content.shape ?? 'square') as SpawnShape
  const eFall = overrides?.edgeFalloff ?? content.edgeFalloff ?? 0
  const [eHalfW, eHalfH] = streuHalbmasse(overrides, SLIDE_WIDTH / 2, SLIDE_HEIGHT / 2)
  const edgeAttr = mesh.geometry.getAttribute('aEdge') as
    | THREE.BufferAttribute
    | undefined

  const m = new THREE.Matrix4()
  const q = new THREE.Quaternion()
  const e = new THREE.Euler()
  const t = new THREE.Vector3()
  const sc = new THREE.Vector3()
  const col = new THREE.Color()
  const base = mesh.userData.baseColors as Float32Array | undefined
  const TAU = Math.PI * 2

  // visible instance count: an active Count channel draws a subset of the
  // max-allocated instances (seed-indexed positions keep the tail in place)
  const alloc =
    (mesh.userData.allocCount as number | undefined) ??
    mesh.instanceMatrix.count
  mesh.count = Math.min(
    Math.max(1, Math.round(overrides?.count ?? content.count ?? 180)),
    alloc
  )
  const count = mesh.count
  for (let i = 0; i < count; i += 1) {
    const sx = seeds[i * 5 + 0]
    const phase = seeds[i * 5 + 1]
    const vfac = seeds[i * 5 + 2]
    const sway = seeds[i * 5 + 3]
    const dz = seeds[i * 5 + 4]
    const fallen = (phase * travel + timer * speed * vfac) % travel
    const y = SLIDE_HEIGHT / 2 + margin - fallen
    const x =
      (sx - 0.5) * SLIDE_WIDTH +
      Math.sin(timer * swayFreq * vfac + sway * TAU) * swayAmp +
      wind * windFac * fallen
    const z = depthRange ? (dz - 0.5) * depthRange : 0

    let ex
    let ey
    let ez
    if (isSnow) {
      // gentle wobble (kept small so flakes stay face-on) + slow in-plane spin
      ex = Math.sin(timer * 0.0006 * vfac + phase * TAU) * 0.5
      ey = Math.sin(timer * 0.0007 * vfac + sx * TAU) * 0.5
      ez = timer * (0.0005 + vfac * 0.0007) + sway * TAU
    } else {
      // confetti: full tumble on every axis
      ex = timer * (0.0012 + vfac * 0.0018) + phase * TAU
      ey = timer * (0.0016 + sway * 0.0022) + sx * TAU
      ez = timer * (0.0008 + dz * 0.0012)
    }
    e.set(ex, ey, ez)
    q.setFromEuler(e)
    t.set(x, y, z)
    // per-piece size variation (no two pieces identical)
    const s = size * (0.55 + sway * 0.9)
    sc.set(s, s * aspectY, s)
    m.compose(t, q, sc)
    mesh.setMatrixAt(i, m)
    if (edgeAttr)
      edgeAttr.setX(
        i,
        edgeAlphaFor(x, y, eShape, eFall, eHalfW, eHalfH)
      )

    // "catch the light": face-on (normal towards camera) is bright, edge-on
    // dims — the rotated +Z normal's z-component is cos(ex)*cos(ey)
    if (base) {
      const facing = Math.abs(Math.cos(ex) * Math.cos(ey))
      const bright = isSnow ? 0.75 + 0.25 * facing : 0.4 + 0.6 * facing
      col.setRGB(
        base[i * 3] * bright,
        base[i * 3 + 1] * bright,
        base[i * 3 + 2] * bright
      )
      mesh.setColorAt(i, col)
    }
  }
  mesh.instanceMatrix.needsUpdate = true
  if (base && mesh.instanceColor) mesh.instanceColor.needsUpdate = true
  if (edgeAttr) edgeAttr.needsUpdate = true
  }

  // Smoke puffs: face the camera (no tumble), rise with turbulence, grow as they
  // go and fade in low / out high so the column dissipates. Deterministic.

export function syncSmoke(
  mesh: THREE.InstancedMesh,
  content: EffectContent,
  timer: number,
  overrides?: EffectOverrides
  ) {
  const seeds = mesh.userData.seeds as Float32Array | undefined
  if (!seeds) return
  const { SLIDE_WIDTH, SLIDE_HEIGHT } = RendererStore
  const margin = SLIDE_HEIGHT * 0.15
  const travel = SLIDE_HEIGHT + margin * 2
  const wind = overrides?.wind ?? content.wind ?? 0
  const size = overrides?.size ?? content.size ?? 60
  const speed =
    (overrides?.speed ?? content.speed ?? 1) * 0.00018 * SLIDE_HEIGHT * 0.4
  const swayAmp = SLIDE_WIDTH * 0.05
  const swayFreq = 0.0011
  const turbAmp = SLIDE_WIDTH * 0.04
  const turbFreq = 0.0022
  const depth = overrides?.depth ?? content.depth ?? 0.3
  const depthRange = depth * Math.min(SLIDE_WIDTH, SLIDE_HEIGHT)

  const m = new THREE.Matrix4()
  const q = new THREE.Quaternion() // identity -> the quad faces the camera
  const t = new THREE.Vector3()
  const sc = new THREE.Vector3()
  const opac = mesh.geometry.getAttribute('aOpacity') as
    | THREE.BufferAttribute
    | undefined
  const edgeAttr = mesh.geometry.getAttribute('aEdge') as
    | THREE.BufferAttribute
    | undefined
  const eShape = (content.shape ?? 'square') as SpawnShape
  const eFall = overrides?.edgeFalloff ?? content.edgeFalloff ?? 0
  const [eHalfW, eHalfH] = streuHalbmasse(overrides, SLIDE_WIDTH / 2, SLIDE_HEIGHT / 2)
  const TAU = Math.PI * 2

  // visible instance count: an active Count channel draws a subset of the
  // max-allocated instances (seed-indexed positions keep the tail in place)
  const alloc =
    (mesh.userData.allocCount as number | undefined) ??
    mesh.instanceMatrix.count
  mesh.count = Math.min(
    Math.max(1, Math.round(overrides?.count ?? content.count ?? 180)),
    alloc
  )
  const count = mesh.count
  for (let i = 0; i < count; i += 1) {
    const sx = seeds[i * 5 + 0]
    const phase = seeds[i * 5 + 1]
    const vfac = seeds[i * 5 + 2]
    const sway = seeds[i * 5 + 3]
    const dz = seeds[i * 5 + 4]
    const fallen = (phase * travel + timer * speed * vfac) % travel
    const progress = fallen / travel // 0 at the source (bottom) -> 1 at top
    const y = -SLIDE_HEIGHT / 2 - margin + fallen
    const x =
      (sx - 0.5) * SLIDE_WIDTH +
      Math.sin(timer * swayFreq * vfac + sway * TAU) * swayAmp +
      Math.sin(timer * turbFreq * vfac + sx * TAU) * turbAmp * progress +
      wind * 0.4 * fallen
    const z = depthRange ? (dz - 0.5) * depthRange : 0
    const s = size * (0.5 + progress * 1.7) // grows as it rises
    t.set(x, y, z)
    sc.set(s, s, s)
    m.compose(t, q, sc)
    mesh.setMatrixAt(i, m)
    if (opac) {
      // dense near the source, dissipating as it climbs
      const fadeIn = Math.min(1, progress / 0.1)
      const fadeOut = 1 - Math.max(0, (progress - 0.35) / 0.65)
      opac.setX(i, Math.max(0, fadeIn * fadeOut))
    }
    if (edgeAttr)
      edgeAttr.setX(
        i,
        edgeAlphaFor(x, y, eShape, eFall, eHalfW, eHalfH)
      )
  }
  mesh.instanceMatrix.needsUpdate = true
  if (opac) opac.needsUpdate = true
  if (edgeAttr) edgeAttr.needsUpdate = true
  }

  // AO and light maps sample the 2nd UV set (uv2). Loaded OBJ/FBX models and
  // the parametric primitives usually only have uv1, so without this the map
  // would silently do nothing. Copy uv → uv2 where it's missing.

export function updateEffect(
  host: EffectHost,layerId: string, content: EffectContent, buildCount?: number) {
  const effect = content.effect ?? 'confetti'
  const existing = host.layerMeshes[layerId] as unknown as THREE.Points | undefined
  // allocate `buildCount` particles when the Count channel is animated (so any
  // subset up to it can be drawn per frame), otherwise the exact static count.
  // Mobil halbiert (Jans Fund 26.08.2026: der Rauch kostete auf dem iPhone
  // sichtbar Frames) — alle Draw-Zaehler klemmen gegen diese Allokation,
  // die Halbierung wirkt damit ueberall.
  const voll = Math.max(1, Math.round(buildCount ?? content.count ?? 180))
  const count = istMobilesGeraet() ? Math.max(1, Math.ceil(voll / 2)) : voll
  // cache key uses the STATIC size; animated size is applied per-frame in
  // syncEffect, so it must not rebuild the system on every animated frame
  const size = content.size ?? 26
  // include the confetti palette in the cache key so editing/resetting the
  // colours actually rebuilds the instanced mesh (colours are baked in at
  // build time). Reset → confettiColors undefined → 'def' → back to default.
  const colorSig =
    effect === 'confetti' && Array.isArray(content.confettiColors)
      ? content.confettiColors
          .map((c: TODOAny) => `${c?.r}_${c?.g}_${c?.b}`)
          .join(',')
      : 'def'
  // confetti piece shape (rect/round) also changes the built material, so it
  // must invalidate the cache
  const pieceSig =
    effect === 'confetti' ? content.pieceShape ?? 'rect' : '-'
  // „Mischt sich in 3D" veraendert das Material (depthTest) — Umschalten im
  // Editor muss das System neu bauen, sonst greift das Haekchen erst nach
  // einem Reload.
  const depthSig = content.depthMix ? 'dm' : '-'
  const key = `${effect}|${count}|${size}|${colorSig}|${pieceSig}|${depthSig}`
  if (existing && existing.userData.effectKey === key) return

  if (existing) {
    existing.geometry.dispose()
    const oldMat = existing.material as THREE.PointsMaterial
    oldMat.map?.dispose()
    oldMat.dispose()
    // free the instance buffers when the previous effect was confetti
    ;(existing as TODOAny).dispose?.()
  }

  const { SLIDE_WIDTH, SLIDE_HEIGHT } = RendererStore
  // deterministic pseudo-random in [0,1) from an integer seed
  const rand = (n: number) => {
    const s = Math.sin(n * 12.9898) * 43758.5453
    return s - Math.floor(s)
  }
  const defaultConfettiPalette = [
    [0.95, 0.26, 0.21],
    [0.99, 0.85, 0.23],
    [0.3, 0.69, 0.31],
    [0.13, 0.59, 0.95],
    [0.61, 0.15, 0.69],
    [1.0, 0.6, 0.0],
  ]
  // user-picked confetti palette (Color 0..255 → 0..1), else the default
  const customConfetti = Array.isArray((content as TODOAny).confettiColors)
    ? ((content as TODOAny).confettiColors as TODOAny[])
        .filter((c) => c)
        .map((c) => [
          (c.r ?? 0) / 255,
          (c.g ?? 0) / 255,
          (c.b ?? 0) / 255,
        ])
    : null
  const confettiPalette =
    customConfetti && customConfetti.length
      ? customConfetti
      : defaultConfettiPalette
  const colorFor = (i: number): [number, number, number] => {
    switch (effect) {
      case 'snow': {
        const t = 0.9 + rand(i + 31) * 0.1
        return [t, t, Math.min(1, t + 0.03)]
      }
      case 'rain':
        return [0.62, 0.74, 0.96]
      case 'sparks':
        return [1.0, 0.7 + 0.3 * rand(i + 41), 0.15 + 0.45 * rand(i + 47)]
      case 'fire':
        // warm yellow -> orange -> deep orange
        return [1.0, 0.42 + 0.42 * rand(i + 41), 0.05 + 0.12 * rand(i + 47)]
      case 'smoke': {
        const t = 0.42 + rand(i + 53) * 0.32
        return [t, t, t * 1.02]
      }
      case 'clouds': {
        const t = 0.9 + rand(i + 59) * 0.1
        return [t, t, Math.min(1, t + 0.02)]
      }
      case 'confetti':
      default:
        return confettiPalette[i % confettiPalette.length] as [
          number,
          number,
          number
        ]
    }
  }

  // Confetti & snow get a dedicated path: real flat quads (InstancedMesh) so
  // each piece tumbles in 3D (flips, catches light, goes edge-on) and varies
  // in size, instead of a flat identical billboard. Confetti = solid coloured
  // rectangles tumbling fast; snow = textured flakes fluttering gently. All
  // other effects stay GPU point sprites below.
  if (effect === 'confetti' || effect === 'snow') {
    const isSnow = effect === 'snow'
    // round confetti pieces: mask the quad with a crisp filled disc so each
    // piece reads as a circle instead of a rectangle
    const pieceShape = content.pieceShape ?? 'rect'
    const isRoundPiece = !isSnow && pieceShape === 'round'
    const isFormPiece =
      !isSnow && (pieceShape === 'heart' || pieceShape === 'star' || pieceShape === 'leaf')
    const geometry = new THREE.PlaneGeometry(1, 1)
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      toneMapped: false,
      // keep the effect compositing like the point version: drawn on top of
      // the layers below, no depth write/test (the tumble + edge-on flip come
      // from the quad geometry, not from depth occlusion).
      // AUSNAHME „Mischt sich in 3D" (Jans Wunsch 13.08.2026): depthTest an —
      // opake 3D-Objekte (Foto-Flaeche, Glaskugel) verdecken dann einzelne
      // Teilchen echt raeumlich. depthWrite bleibt aus: die Teilchen sollen
      // einander nicht wegstanzen (halbtransparente Kanten + Sortierung).
      transparent: true,
      depthWrite: false,
      depthTest: content.depthMix === true,
      // snow flakes have a real flake shape; confetti are solid rectangles,
      // or a filled disc when the piece shape is "round"
      map: isSnow
        ? makeFlakeTexture()
        : isRoundPiece
        ? makeParticleTexture('confetti')
        : isFormPiece
        ? makePieceTexture(pieceShape as 'heart' | 'star' | 'leaf')
        : null,
      alphaTest: isSnow ? 0.04 : isRoundPiece || isFormPiece ? 0.5 : 0,
    })
    injectEdgeAlpha(material)
    const mesh = new THREE.InstancedMesh(geometry, material, count)
    mesh.frustumCulled = false
    geometry.setAttribute(
      'aEdge',
      new THREE.InstancedBufferAttribute(new Float32Array(count).fill(1), 1)
    )
    const seedsC = new Float32Array(count * 5)
    const baseC = new Float32Array(count * 3)
    const tmpColor = new THREE.Color()
    for (let i = 0; i < count; i += 1) {
      seedsC[i * 5 + 0] = rand(i + 1)
      seedsC[i * 5 + 1] = rand(i + 7.3)
      seedsC[i * 5 + 2] = 0.6 + rand(i + 13.1) * 0.8
      seedsC[i * 5 + 3] = rand(i + 19.7)
      seedsC[i * 5 + 4] = rand(i + 23.5)
      const [r, g, b] = colorFor(i)
      baseC[i * 3 + 0] = r
      baseC[i * 3 + 1] = g
      baseC[i * 3 + 2] = b
      mesh.setColorAt(i, tmpColor.setRGB(r, g, b))
    }
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true
    mesh.name = `effect:${effect}`
    mesh.userData.effectKey = key
    mesh.userData.seeds = seedsC
    mesh.userData.baseColors = baseC
    mesh.userData.instanced = true
    mesh.userData.allocCount = count
    // draw only the static count up front; an active Count channel adjusts
    // this per frame in syncInstanced
    mesh.count = Math.min(Math.max(1, Math.round(content.count ?? 180)), count)
    host.layerMeshes[layerId] = mesh as unknown as THREE.Mesh
    host.layerContentCache[layerId] = content
    if (WebGLAssets.allLayersSet) host.props.renderer.loadedLayer(layerId, key)
    return
  }

  // Smoke: instanced soft puffs that face the camera, grow as they rise and
  // fade in/out over their life (a per-instance opacity attribute injected
  // into the basic material drives the fade).
  if (effect === 'smoke') {
    const geometry = new THREE.PlaneGeometry(1, 1)
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      toneMapped: false,
      transparent: true,
      depthWrite: false,
      // s. Konfetti-Pfad: "Mischt sich in 3D" schaltet den Tiefen-Test an.
      depthTest: content.depthMix === true,
      map: makeParticleTexture('smoke'),
      opacity: 0.6,
    })
    material.onBeforeCompile = (shader) => {
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          '#include <common>\nattribute float aOpacity;\nattribute float aEdge;\nvarying float vAOpacity;\nvarying float vEdge;'
        )
        .replace(
          '#include <begin_vertex>',
          '#include <begin_vertex>\nvAOpacity = aOpacity;\nvEdge = aEdge;'
        )
      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          '#include <common>\nvarying float vAOpacity;\nvarying float vEdge;'
        )
        .replace(
          '#include <dithering_fragment>',
          '#include <dithering_fragment>\ngl_FragColor.a *= vAOpacity * vEdge;'
        )
    }
    const mesh = new THREE.InstancedMesh(geometry, material, count)
    mesh.frustumCulled = false
    geometry.setAttribute(
      'aEdge',
      new THREE.InstancedBufferAttribute(new Float32Array(count).fill(1), 1)
    )
    const seedsC = new Float32Array(count * 5)
    const opac = new Float32Array(count)
    const tmpColor = new THREE.Color()
    for (let i = 0; i < count; i += 1) {
      seedsC[i * 5 + 0] = rand(i + 1)
      seedsC[i * 5 + 1] = rand(i + 7.3)
      seedsC[i * 5 + 2] = 0.6 + rand(i + 13.1) * 0.8
      seedsC[i * 5 + 3] = rand(i + 19.7)
      seedsC[i * 5 + 4] = rand(i + 23.5)
      opac[i] = 1
      const [r, g, b] = colorFor(i)
      mesh.setColorAt(i, tmpColor.setRGB(r, g, b))
    }
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true
    geometry.setAttribute(
      'aOpacity',
      new THREE.InstancedBufferAttribute(opac, 1)
    )
    mesh.name = `effect:${effect}`
    mesh.userData.effectKey = key
    mesh.userData.seeds = seedsC
    mesh.userData.instanced = true
    mesh.userData.smoke = true
    mesh.userData.allocCount = count
    mesh.count = Math.min(Math.max(1, Math.round(content.count ?? 180)), count)
    host.layerMeshes[layerId] = mesh as unknown as THREE.Mesh
    host.layerContentCache[layerId] = content
    if (WebGLAssets.allLayersSet) host.props.renderer.loadedLayer(layerId, key)
    return
  }

  const positions = new Float32Array(count * 3)
  const colors = new Float32Array(count * 3)
  // per-particle seeds: x start (0..1), phase (0..1), speed factor, sway, depth
  const seeds = new Float32Array(count * 5)
  for (let i = 0; i < count; i += 1) {
    seeds[i * 5 + 0] = rand(i + 1) // start x (normalized)
    seeds[i * 5 + 1] = rand(i + 7.3) // vertical phase
    seeds[i * 5 + 2] = 0.6 + rand(i + 13.1) * 0.8 // speed factor
    seeds[i * 5 + 3] = rand(i + 19.7) // sway phase
    seeds[i * 5 + 4] = rand(i + 23.5) // depth (z) seed
    const [r, g, b] = colorFor(i)
    colors[i * 3 + 0] = r
    colors[i * 3 + 1] = g
    colors[i * 3 + 2] = b
    // initial positions (overwritten each frame)
    positions[i * 3 + 0] = (seeds[i * 5 + 0] - 0.5) * SLIDE_WIDTH
    positions[i * 3 + 1] = (seeds[i * 5 + 1] - 0.5) * SLIDE_HEIGHT
    positions[i * 3 + 2] = 0
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
  // per-particle edge alpha (round area / falloff); updated each frame
  geometry.setAttribute(
    'aEdge',
    new THREE.BufferAttribute(new Float32Array(count).fill(1), 1)
  )

  // sparks/fire glow (additive); soft smoke/clouds stay semi-transparent
  const additive = effect === 'sparks' || effect === 'fire'
  // TypeScript erkennt, dass 'smoke' weiter oben bereits abgezweigt ist —
  // der Vergleich ist tot. Er bleibt hier stehen, weil er in Jans Fassung
  // steht und ein Umbau Verhalten aendern koennte; der Cast beruhigt nur
  // die Typpruefung. (Bei Jan ist es einer seiner 30 offenen Typfehler.)
  const soft = (effect as string) === 'smoke' || effect === 'clouds'
  const material = new THREE.PointsMaterial({
    size,
    vertexColors: true,
    transparent: true,
    depthWrite: false,
    // s. Konfetti-Pfad: "Mischt sich in 3D" schaltet den Tiefen-Test an.
    depthTest: content.depthMix === true,
    sizeAttenuation: true,
    // a procedural sprite gives each particle a real shape instead of the
    // default square GL point; the white shape is tinted by the vertex color
    map: makeParticleTexture(effect),
    // soft puffs must not clip their gradient edge
    alphaTest: soft ? 0 : 0.02,
    opacity: (effect as string) === 'smoke' ? 0.5 : effect === 'clouds' ? 0.72 : 1,
    blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
  })
  injectEdgeAlpha(material)

  const points = new THREE.Points(geometry, material)
  points.name = `effect:${effect}`
  points.userData.effectKey = key
  points.userData.seeds = seeds
  // sparks/fire twinkle & fade by modulating brightness per frame, so keep the
  // base colour to modulate from
  if (effect === 'sparks' || effect === 'fire') {
    points.userData.baseColors = colors.slice()
  }
  points.frustumCulled = false
  points.userData.allocCount = count
  // draw only the static count up front; an active Count channel adjusts the
  // draw range per frame in syncEffect
  geometry.setDrawRange(
    0,
    Math.min(Math.max(1, Math.round(content.count ?? 180)), count)
  )
  host.layerMeshes[layerId] = points as unknown as THREE.Mesh
  host.layerContentCache[layerId] = content
  if (WebGLAssets.allLayersSet) host.props.renderer.loadedLayer(layerId, key)
  }

  // Deterministic per-frame particle placement. Each particle travels across the
  // stage (down for confetti/snow/rain, up for sparks), looping, with a per-
  // effect sine sway and optional wind tilt. Because every position is a pure
  // function of the timeline timer, scrubbing (and the production export)
  // reproduces identical frames.

export function syncEffect(
  points: THREE.Points,
  content: EffectContent,
  timer: number,
  overrides?: EffectOverrides
  ) {
  const seeds = points.userData.seeds as Float32Array | undefined
  if (!seeds) return
  const effect = content.effect ?? 'confetti'
  // confetti/snow/smoke are InstancedMeshes (quads), not point clouds
  if (points.userData.instanced) {
    const im = points as unknown as THREE.InstancedMesh
    if (points.userData.smoke) syncSmoke(im, content, timer, overrides)
    else syncInstanced(im, content, timer, overrides)
    return
  }
  const pos = points.geometry.getAttribute('position') as THREE.BufferAttribute
  const { SLIDE_WIDTH, SLIDE_HEIGHT } = RendererStore
  // round-area / edge-falloff alpha (updated per particle below)
  const eShape = (content.shape ?? 'square') as SpawnShape
  const eFall = overrides?.edgeFalloff ?? content.edgeFalloff ?? 0
  const [eHalfW, eHalfH] = streuHalbmasse(overrides, SLIDE_WIDTH / 2, SLIDE_HEIGHT / 2)
  const edgeAttr = points.geometry.getAttribute('aEdge') as
    | THREE.BufferAttribute
    | undefined
  const margin = SLIDE_HEIGHT * 0.15
  const travel = SLIDE_HEIGHT + margin * 2
  const wind = overrides?.wind ?? content.wind ?? 0

  // per-effect tuning
  let vMul = 1
  let swayAmp = SLIDE_WIDTH * 0.04
  let swayFreq = 0.0016
  let windFac = 0
  let up = false
  // secondary, higher-frequency sway -> organic turbulence (fire/smoke/sparks)
  let turbAmp = 0
  let turbFreq = 0.005
  switch (effect) {
    case 'snow':
      vMul = 0.45
      swayAmp = SLIDE_WIDTH * 0.06
      swayFreq = 0.0009
      windFac = 0.45
      break
    case 'rain':
      vMul = 2.6
      swayAmp = SLIDE_WIDTH * 0.004
      swayFreq = 0.002
      windFac = 0.5
      break
    case 'sparks':
      vMul = 0.7
      swayAmp = SLIDE_WIDTH * 0.03
      swayFreq = 0.0012
      turbAmp = SLIDE_WIDTH * 0.02
      turbFreq = 0.0035
      up = true
      break
    case 'fire':
      vMul = 1.4
      swayAmp = SLIDE_WIDTH * 0.05
      swayFreq = 0.004
      turbAmp = SLIDE_WIDTH * 0.035
      turbFreq = 0.009
      up = true
      break
    case 'smoke':
      vMul = 0.4
      swayAmp = SLIDE_WIDTH * 0.05
      swayFreq = 0.0011
      turbAmp = SLIDE_WIDTH * 0.04
      turbFreq = 0.0022
      up = true
      break
    case 'confetti':
    default:
      break
  }
  const speed =
    (overrides?.speed ?? content.speed ?? 1) * 0.00018 * SLIDE_HEIGHT * vMul

  // spatial depth: spread particles through z for parallax (perspective cam)
  const depth =
    overrides?.depth ?? content.depth ?? (effect === 'clouds' ? 0.7 : 0)
  const depthRange = depth * Math.min(SLIDE_WIDTH, SLIDE_HEIGHT)

  // visible particle count: an active Count channel draws a subset of the
  // max-allocated buffer (no rebuild → no flicker); positions are seed-indexed
  // so the hidden tail stays put
  const alloc = (points.userData.allocCount as number | undefined) ?? pos.count
  const count = Math.min(
    Math.max(1, Math.round(overrides?.count ?? content.count ?? 180)),
    alloc
  )
  points.geometry.setDrawRange(0, count)
  const size = overrides?.size ?? content.size ?? 26
  // particle size is baked into the material at build time; push the (possibly
  // animated) size each frame so an active Size keyframe channel takes effect
  ;(points.material as THREE.PointsMaterial).size = size

  // tilt the rain streaks to match the wind drift (vertical when wind = 0)
  if (effect === 'rain') {
    // BEFUND: `PointsMaterial` hat kein `rotation` — three ignoriert die
    // Zuweisung. Regen wird also AUCH IN JANS RENDERER nicht schraeg
    // gestellt. Verhaltensgleich uebernommen (die Eigenschaft wird gesetzt
    // und ignoriert), damit der Port nichts still repariert. Bei ihm ist es
    // einer der 30 offenen Typfehler — meldenswert.
    ;(points.material as unknown as { rotation: number }).rotation = -Math.atan(
      wind * windFac * 1.2
    )
  }

  // clouds drift sideways; puffs are grouped into clusters so they read as a
  // few lumpy clouds rather than evenly scattered dots
  if (effect === 'clouds') {
    const cMargin = SLIDE_WIDTH * 0.25
    const cTravel = SLIDE_WIDTH + cMargin * 2
    const cSpeed =
      (overrides?.speed ?? content.speed ?? 1) * 0.00006 * SLIDE_WIDTH
    const dir = wind < 0 ? -1 : 1
    const puffsPerCloud = 6
    // deterministic per-cluster pseudo-random
    const crand = (n: number) => {
      const s = Math.sin(n * 43.123) * 7219.13
      return s - Math.floor(s)
    }
    for (let i = 0; i < count; i += 1) {
      const sx = seeds[i * 5 + 0]
      const sy = seeds[i * 5 + 1]
      const vfac = seeds[i * 5 + 2]
      const dz = seeds[i * 5 + 4]
      const ci = Math.floor(i / puffsPerCloud)
      const cBaseX = crand(ci + 1)
      const cBaseY = crand(ci + 50)
      const cVfac = 0.7 + crand(ci + 90) * 0.6
      const moved = (cBaseX * cTravel + timer * cSpeed * cVfac) % cTravel
      const clusterX =
        dir > 0
          ? -SLIDE_WIDTH / 2 - cMargin + moved
          : SLIDE_WIDTH / 2 + cMargin - moved
      const clusterY = (cBaseY - 0.5) * SLIDE_HEIGHT * 0.7
      // puff offset within its cloud (a soft horizontal lump)
      const lx = (sx - 0.5) * size * 1.3
      const ly = (sy - 0.5) * size * 0.55
      const x = clusterX + lx
      const y =
        clusterY +
        ly +
        Math.sin(timer * 0.0004 * vfac + sx * 6.2832) * SLIDE_HEIGHT * 0.01
      pos.setXYZ(i, x, y, (dz - 0.5) * depthRange)
      if (edgeAttr)
        edgeAttr.setX(
          i,
          edgeAlphaFor(x, y, eShape, eFall, eHalfW, eHalfH)
        )
    }
    pos.needsUpdate = true
    if (edgeAttr) edgeAttr.needsUpdate = true
    return
  }

  // sparks & fire modulate brightness per frame (twinkle / fade as they rise)
  const modColor = effect === 'sparks' || effect === 'fire'
  const base = modColor
    ? (points.userData.baseColors as Float32Array | undefined)
    : undefined
  const colAttr = base
    ? (points.geometry.getAttribute('color') as THREE.BufferAttribute)
    : undefined

  for (let i = 0; i < count; i += 1) {
    const sx = seeds[i * 5 + 0]
    const phase = seeds[i * 5 + 1]
    const vfac = seeds[i * 5 + 2]
    const sway = seeds[i * 5 + 3]
    const dz = seeds[i * 5 + 4]
    const fallen = (phase * travel + timer * speed * vfac) % travel
    const y = up
      ? -SLIDE_HEIGHT / 2 - margin + fallen
      : SLIDE_HEIGHT / 2 + margin - fallen
    // smoke disperses more as it rises; fire/sparks turbulence is steady
    const turb =
      turbAmp === 0
        ? 0
        : Math.sin(timer * turbFreq * vfac + sx * 6.2832) *
          turbAmp *
          ((effect as string) === 'smoke' ? fallen / travel : 1)
    const x =
      (sx - 0.5) * SLIDE_WIDTH +
      Math.sin(timer * swayFreq * vfac + sway * 6.2832) * swayAmp +
      turb +
      wind * windFac * fallen
    pos.setXYZ(i, x, y, depthRange ? (dz - 0.5) * depthRange : 0)
    if (edgeAttr)
      edgeAttr.setX(i, edgeAlphaFor(x, y, eShape, eFall, eHalfW, eHalfH))

    if (colAttr && base) {
      const flick =
        0.5 + 0.5 * Math.sin(timer * 0.004 * vfac + sway * 6.2832)
      let tw
      if (effect === 'fire') {
        // bright at the base, fading to nothing as the ember rises
        const life = Math.max(0, 1 - fallen / travel)
        tw = life * (0.55 + 0.45 * flick)
      } else {
        tw = 0.3 + 0.7 * flick
      }
      colAttr.setXYZ(
        i,
        base[i * 3] * tw,
        base[i * 3 + 1] * tw,
        base[i * 3 + 2] * tw
      )
    }
  }
  pos.needsUpdate = true
  if (colAttr) colAttr.needsUpdate = true
  if (edgeAttr) edgeAttr.needsUpdate = true
  }

  // Confetti & snow: each piece is a real flat quad that falls/sways like the
  // point version but also rotates and varies in size per piece. Confetti tumble
  // hard on all axes (flip, catch the light, go edge-on); snow flutters gently
  // (mostly facing the viewer, slow spin) so the flakes stay readable.
  // Deterministic from the timer, so scrubbing reproduces identical frames.
